import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useScopedCompanyId } from "@/owner/useScopedCompanyId";
import { getCashFlow, type CashFlowSummary } from "@/utils/cash-flow.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocaleFormat } from "@/lib/i18n-format";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  Package,
  Download,
  ArrowDownCircle,
  ArrowUpCircle,
  ShieldAlert,
  RefreshCw,
  ExternalLink,
  FileSignature,
  Building2,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useChartTheme } from "@/lib/chart-theme";
import { CashFlowChart } from "@/components/cash-flow/CashFlowChart";
import {
  CashFlowChartTypePicker,
  useCashFlowChartType,
} from "@/components/cash-flow/CashFlowChartTypePicker";

export const Route = createFileRoute("/_app/cash-flow")({
  component: CashFlowPage,
  head: () => ({
    meta: [
      { title: "Fluxo de Caixa — Business Dite Ads" },
      {
        name: "description",
        content: "Entradas, saídas e saldo líquido da empresa por período.",
      },
    ],
  }),
});

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function monthsAgoISO(n: number) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}
/** First day (ISO) of the month `offset` months from the current one (0 = this month, -1 = last month, …). */
function monthStartISO(offset: number) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 10);
}
/** Last day (ISO) of the month `offset` months from the current one. */
function monthEndISO(offset: number) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}

type PeriodPreset = "1m" | "1y";

function presetToStartDate(preset: PeriodPreset, monthOffset: number): string {
  switch (preset) {
    case "1m":
      // Calendar month (day 1 → last day, or today for the current month), so
      // the chart can break it down into the 4 weeks of that month.
      return monthStartISO(monthOffset);
    case "1y":
      return monthsAgoISO(12);
  }
}

/** Week-of-month bucket for a day-of-month number: 1-7→1, 8-14→2, 15-21→3, 22+→4. */
function weekOfMonth(dayOfMonth: number): number {
  return Math.min(4, Math.floor((dayOfMonth - 1) / 7) + 1);
}

type WeekBucket = {
  week: number;
  inflow: number;
  expenseOut: number;
  payrollOut: number;
  materialsOut: number;
  totalOut: number;
  net: number;
};

function CashFlowPage() {
  const { t, i18n } = useTranslation();
  const { company } = useAuth();
  const scopedCompanyId = useScopedCompanyId();
  const { formatMoney } = useLocaleFormat();
  const currency = company?.currency ?? "USD";
  const formatCurrency = (n: number, cur = "USD") => formatMoney(n, cur);
  const chartTheme = useChartTheme();
  const isDark = chartTheme.id !== "default";
  const [chartVariant, setChartVariant] = useCashFlowChartType();

  const PERIOD_PRESETS: Array<{ value: PeriodPreset; label: string }> = [
    { value: "1m", label: t("cashFlow.filters.periodMonth") },
    { value: "1y", label: t("cashFlow.filters.periodYear") },
  ];

  const [period, setPeriod] = useState<PeriodPreset>("1m");
  // Month mode only: 0 = current calendar month, -1 = previous month, etc.
  // Ignored in year mode (1y always looks back 12 months from today).
  const [monthOffset, setMonthOffset] = useState(0);
  const periodStart = useMemo(
    () => presetToStartDate(period, monthOffset),
    [period, monthOffset],
  );
  const periodEnd = useMemo(() => {
    if (period === "1m") {
      // Current month: month-to-date (today). Past months: the full month.
      return monthOffset === 0 ? todayISO() : monthEndISO(monthOffset);
    }
    return todayISO();
  }, [period, monthOffset]);
  const [data, setData] = useState<CashFlowSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [clientId, setClientId] = useState<string>("all");
  const [projectId, setProjectId] = useState<string>("all");
  // Month mode only: null = 4-week overview, 1..4 = drilled into that week (daily detail).
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [projects, setProjects] = useState<
    Array<{ id: string; name: string; code: string; client_id: string }>
  >([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    let projectsQ = supabase
      .from("projects")
      .select("id, name, code, client_id")
      .order("name", { ascending: true });
    if (scopedCompanyId) projectsQ = projectsQ.eq("company_id", scopedCompanyId);
    void projectsQ.then(({ data: rows, error: err }) => {
      if (cancelled || err) return;
      setProjects(rows ?? []);
    });
    let clientsQ = supabase
      .from("clients")
      .select("id, name")
      .eq("is_active", true)
      .order("name", { ascending: true });
    if (scopedCompanyId) clientsQ = clientsQ.eq("company_id", scopedCompanyId);
    void clientsQ.then(({ data: rows, error: err }) => {
      if (cancelled || err) return;
      setClients(rows ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [scopedCompanyId]);

  useEffect(() => {
    if (clientId === "all") return;
    if (projectId === "all") return;
    const proj = projects.find((p) => p.id === projectId);
    if (!proj || proj.client_id !== clientId) {
      setProjectId("all");
    }
  }, [clientId, projectId, projects]);

  useEffect(() => {
    setClientId("all");
    setProjectId("all");
  }, [scopedCompanyId]);

  // Selecting a week is a client-side drill-down into the current month; any
  // change to the period, the selected month, or the active filters should
  // drop back to the month/year overview instead of pointing at a now-stale
  // week.
  useEffect(() => {
    setSelectedWeek(null);
  }, [period, monthOffset, clientId, projectId, scopedCompanyId]);

  useEffect(() => {
    let cancelled = false;
    const reload = (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      setError(null);
      return getCashFlow({
        data: {
          periodStart,
          periodEnd,
          projectId: projectId === "all" ? null : projectId,
          clientId: clientId === "all" ? null : clientId,
          companyId: scopedCompanyId,
        },
      })
        .then((res) => {
          if (!cancelled) setData(res);
        })
        .catch((e) => {
          if (!cancelled) setError(e?.message ?? t("cashFlow.errors.loadFailed"));
        })
        .finally(() => {
          if (!cancelled && showSpinner) setLoading(false);
        });
    };

    void reload(true);

    const liveCompanyId = scopedCompanyId;
    const channelKey = liveCompanyId ?? "all";
    const channel = supabase
      .channel(`cash-flow-live-${channelKey}`)
      .on(
        "postgres_changes",
        liveCompanyId
          ? {
              event: "UPDATE",
              schema: "public",
              table: "payroll_runs",
              filter: `company_id=eq.${liveCompanyId}`,
            }
          : { event: "UPDATE", schema: "public", table: "payroll_runs" },
        (payload) => {
          const newRow = payload.new as { status?: string; number?: string } | null;
          const oldStatus = (payload.old as { status?: string } | null)?.status;
          if (newRow?.status === "paid" && oldStatus !== "paid") {
            toast.success(t("cashFlow.chart.savedToast"), {
              description: newRow.number
                ? t("cashFlow.chart.payrollPaidWithNumber", { number: newRow.number })
                : t("cashFlow.chart.payrollPaid"),
            });
            void reload(false);
          }
        },
      )
      .on(
        "postgres_changes",
        liveCompanyId
          ? {
              event: "*",
              schema: "public",
              table: "invoice_payments",
              filter: `company_id=eq.${liveCompanyId}`,
            }
          : { event: "*", schema: "public", table: "invoice_payments" },
        () => void reload(false),
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [periodStart, periodEnd, scopedCompanyId, refreshNonce, projectId, clientId, t]);

  // Month mode: bucket `byDay` into the 4 weeks-of-month (1-7/8-14/15-21/22-end).
  // For the current month (monthOffset 0), weeks are only emitted up to the
  // week containing today (periodEnd = today), so the overview and the ‹ ›
  // navigation never point at a week that hasn't happened yet. For past
  // months, periodEnd is the last day of that month, so all 4 weeks show.
  const monthWeeks = useMemo<WeekBucket[]>(() => {
    if (period !== "1m") return [];
    const days = data?.byDay ?? [];
    const buckets = new Map<number, WeekBucket>();
    const ensureWeek = (w: number) => {
      let b = buckets.get(w);
      if (!b) {
        b = { week: w, inflow: 0, expenseOut: 0, payrollOut: 0, materialsOut: 0, totalOut: 0, net: 0 };
        buckets.set(w, b);
      }
      return b;
    };
    let maxWeek = weekOfMonth(new Date(`${periodEnd}T00:00:00`).getDate());
    for (const d of days) {
      const dayOfMonth = Number(d.day.slice(8, 10));
      const w = weekOfMonth(dayOfMonth);
      maxWeek = Math.max(maxWeek, w);
      const bucket = ensureWeek(w);
      bucket.inflow += d.inflow;
      bucket.expenseOut += d.expenseOut;
      bucket.payrollOut += d.payrollOut;
      bucket.materialsOut += d.materialsOut;
    }
    const result: WeekBucket[] = [];
    for (let w = 1; w <= Math.min(4, maxWeek); w++) {
      const b = ensureWeek(w);
      b.totalOut = b.expenseOut + b.payrollOut + b.materialsOut;
      b.net = b.inflow - b.totalOut;
      result.push(b);
    }
    return result;
  }, [period, data, periodEnd, monthOffset]);

  // Label for the month navigator, e.g. "julho de 2026" (localized, long form).
  const monthLabel = useMemo(() => {
    const base = new Date();
    base.setDate(1);
    base.setMonth(base.getMonth() + monthOffset);
    return base.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });
  }, [monthOffset, i18n.language]);

  const selectedWeekDays = useMemo(() => {
    if (period !== "1m" || selectedWeek == null) return [];
    return (data?.byDay ?? []).filter((d) => weekOfMonth(Number(d.day.slice(8, 10))) === selectedWeek);
  }, [period, selectedWeek, data]);

  const chartData = useMemo(() => {
    if (period === "1y") {
      return (data?.byMonth ?? []).map((m) => ({
        month: m.month,
        Entradas: Number(m.inflow.toFixed(2)),
        Despesas: Number(m.expenseOut.toFixed(2)),
        Folha: Number(m.payrollOut.toFixed(2)),
        Materiais: Number(m.materialsOut.toFixed(2)),
        Saldo: Number(m.net.toFixed(2)),
      }));
    }
    if (selectedWeek != null) {
      return selectedWeekDays.map((d) => ({
        month: d.day,
        Entradas: Number(d.inflow.toFixed(2)),
        Despesas: Number(d.expenseOut.toFixed(2)),
        Folha: Number(d.payrollOut.toFixed(2)),
        Materiais: Number(d.materialsOut.toFixed(2)),
        Saldo: Number(d.net.toFixed(2)),
      }));
    }
    return monthWeeks.map((w) => ({
      month: t("cashFlow.filters.weekLabel", { n: w.week }),
      Entradas: Number(w.inflow.toFixed(2)),
      Despesas: Number(w.expenseOut.toFixed(2)),
      Folha: Number(w.payrollOut.toFixed(2)),
      Materiais: Number(w.materialsOut.toFixed(2)),
      Saldo: Number(w.net.toFixed(2)),
    }));
  }, [period, selectedWeek, selectedWeekDays, monthWeeks, data, t]);

  // Cards (Entradas/Saídas/Lucro): reflect the selected week when one is
  // active in month mode; otherwise the whole requested period (month/year).
  const selectedWeekTotals = useMemo(() => {
    if (period !== "1m" || selectedWeek == null) return null;
    const w = monthWeeks.find((wk) => wk.week === selectedWeek);
    if (!w) return { inflow: 0, outflow: 0, net: 0 };
    return { inflow: w.inflow, outflow: w.totalOut, net: w.net };
  }, [period, selectedWeek, monthWeeks]);

  const displayInflow = selectedWeekTotals ? selectedWeekTotals.inflow : (data?.totalInflow ?? 0);
  const displayOutflow = selectedWeekTotals ? selectedWeekTotals.outflow : (data?.totalOutflow ?? 0);
  const displayNet = selectedWeekTotals ? selectedWeekTotals.net : (data?.net ?? 0);

  function exportCSV() {
    if (!data) return;
    // Export the granularity currently visible on screen: months (year mode),
    // weeks (month overview), or days (a drilled-into week).
    let rows: (string | number)[][];
    if (period === "1y") {
      rows = [
        ["Month", "Inflows", "Expenses", "Payroll", "Materials", "Total Outflows", "Net"],
        ...data.byMonth.map((m) => [
          m.month,
          m.inflow.toFixed(2),
          m.expenseOut.toFixed(2),
          m.payrollOut.toFixed(2),
          m.materialsOut.toFixed(2),
          m.totalOut.toFixed(2),
          m.net.toFixed(2),
        ]),
      ];
    } else if (selectedWeek != null) {
      rows = [
        ["Day", "Inflows", "Expenses", "Payroll", "Materials", "Total Outflows", "Net"],
        ...selectedWeekDays.map((d) => [
          d.day,
          d.inflow.toFixed(2),
          d.expenseOut.toFixed(2),
          d.payrollOut.toFixed(2),
          d.materialsOut.toFixed(2),
          d.totalOut.toFixed(2),
          d.net.toFixed(2),
        ]),
      ];
    } else {
      rows = [
        ["Week", "Inflows", "Expenses", "Payroll", "Materials", "Total Outflows", "Net"],
        ...monthWeeks.map((w) => [
          `Week ${w.week}`,
          w.inflow.toFixed(2),
          w.expenseOut.toFixed(2),
          w.payrollOut.toFixed(2),
          w.materialsOut.toFixed(2),
          w.totalOut.toFixed(2),
          w.net.toFixed(2),
        ]),
      ];
    }
    rows.push(
      [],
      ["Total Inflows", displayInflow.toFixed(2)],
      ["Total Outflows", displayOutflow.toFixed(2)],
      ["Net", displayNet.toFixed(2)],
    );
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const weekSuffix = period === "1m" && selectedWeek != null ? `-week${selectedWeek}` : "";
    a.download = `cash-flow-${periodStart}-to-${periodEnd}${weekSuffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const contractsCount = data?.contractsCount ?? 0;
  const changeOrdersTotal = data?.changeOrdersTotal ?? 0;
  const changeOrdersCount = data?.changeOrdersCount ?? 0;


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("cashFlow.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("cashFlow.subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data}>
          <Download className="mr-2 h-4 w-4" />
          {t("cashFlow.exportCsv")}
        </Button>
      </div>

      <Card className="flex flex-wrap items-center gap-3 p-3">
        <div className="flex items-center gap-1">
          {PERIOD_PRESETS.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={period === p.value ? "default" : "outline"}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </Button>
          ))}
        </div>
        {period === "1m" && (
          <div className="flex flex-wrap items-center gap-1 border-l border-border/60 pl-3">
            {selectedWeek == null ? (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  onClick={() => setMonthOffset((o) => o - 1)}
                  aria-label={t("common.previous")}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 text-sm font-medium capitalize">{monthLabel}</span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  disabled={monthOffset === 0}
                  onClick={() => setMonthOffset((o) => o + 1)}
                  aria-label={t("common.next")}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {monthWeeks.length > 0 && (
                  <div className="ml-2 flex flex-wrap items-center gap-1 border-l border-border/60 pl-2">
                    {monthWeeks.map((w) => (
                      <Button
                        key={w.week}
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedWeek(w.week)}
                      >
                        {t("cashFlow.filters.weekLabel", { n: w.week })}
                      </Button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  disabled={selectedWeek <= 1}
                  onClick={() => setSelectedWeek((w) => Math.max(1, (w ?? 1) - 1))}
                  aria-label={t("common.previous")}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 text-sm font-medium">
                  {t("cashFlow.filters.weekLabel", { n: selectedWeek })}
                </span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  disabled={selectedWeek >= monthWeeks.length}
                  onClick={() =>
                    setSelectedWeek((w) => Math.min(monthWeeks.length, (w ?? 1) + 1))
                  }
                  aria-label={t("common.next")}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedWeek(null)}>
                  {t("cashFlow.filters.backToMonth")}
                </Button>
              </>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="h-9 w-[220px]">
              <SelectValue placeholder={t("cashFlow.filters.filterByClient")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("cashFlow.filters.allClients")}</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="h-9 w-[260px]">
              <SelectValue placeholder={t("cashFlow.filters.filterByProject")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("cashFlow.filters.allProjects")}</SelectItem>
              {projects
                .filter((p) => clientId === "all" || p.client_id === clientId)
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.code ? `${p.code} — ` : ""}
                    {p.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {error &&
        (() => {
          const lower = error.toLowerCase();
          const isRls =
            lower.includes("row-level security") ||
            lower.includes("row level security") ||
            lower.includes("policy") ||
            lower.includes("permission denied") ||
            lower.includes("infinite recursion") ||
            lower.includes("rls");
          return (
            <Card className="border-destructive/50 bg-destructive/5 p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div>
                    <h3 className="text-sm font-semibold text-destructive">
                      {isRls ? t("cashFlow.errors.rlsTitle") : t("cashFlow.errors.genericTitle")}
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {isRls ? t("cashFlow.errors.rlsBody") : t("cashFlow.errors.genericBody")}
                    </p>
                  </div>
                  <pre className="max-h-32 overflow-auto rounded bg-muted p-2 text-[11px] text-muted-foreground">
                    {error}
                  </pre>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setError(null);
                        setRefreshNonce((n) => n + 1);
                      }}
                    >
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                      {t("cashFlow.errors.retry")}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a
                        href="https://supabase.com/dashboard/project/jvloowliukygmoagvxbq/logs/postgres-logs"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        {t("cashFlow.errors.viewLogs")}
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })()}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileSignature className="h-4 w-4 text-sky-500" />
            {t("cashFlow.kpis.contracts")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-sky-600">
            {formatCurrency((data?.contractsTotal ?? 0) + changeOrdersTotal, currency)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("cashFlow.kpis.contractsCount", { count: contractsCount })}
          </p>
          {changeOrdersCount > 0 && (
            <p className="mt-0.5 text-xs text-violet-600">
              {t("cashFlow.kpis.changeOrdersExtra", {
                value: formatCurrency(changeOrdersTotal, currency),
                count: changeOrdersCount,
              })}
            </p>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowDownCircle className="h-4 w-4 text-emerald-500" />
            {t("cashFlow.kpis.inflows")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-emerald-600">
            {formatCurrency(displayInflow, currency)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("cashFlow.kpis.receivable", {
              value: formatCurrency(data?.outstandingInvoices ?? 0, currency),
            })}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowUpCircle className="h-4 w-4 text-rose-500" />
            {t("cashFlow.kpis.outflows")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-rose-600">
            {formatCurrency(displayOutflow, currency)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("cashFlow.kpis.pending", {
              value: formatCurrency(
                (data?.pendingExpenses ?? 0) + (data?.upcomingPayroll ?? 0),
                currency,
              ),
            })}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Wallet className="h-4 w-4" />
            {t("cashFlow.kpis.profit")}
          </div>
          <div
            className={`mt-1 text-2xl font-semibold ${
              displayNet >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            {formatCurrency(displayNet, currency)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("cashFlow.kpis.profitHint")}</p>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t("cashFlow.outflowBreakdown.title")}</h2>
          <span className="text-xs text-muted-foreground">
            {t("cashFlow.outflowBreakdown.total", {
              value: formatCurrency(data?.totalOutflow ?? 0, currency),
            })}
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Wallet className="h-4 w-4 text-orange-500" />
              {t("cashFlow.outflowBreakdown.payrollPaid")}
            </div>
            <div className="mt-1 text-xl font-semibold">
              {formatCurrency(data?.payrollTotal ?? 0, currency)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("cashFlow.outflowBreakdown.payrollUpcoming", {
                value: formatCurrency(data?.upcomingPayroll ?? 0, currency),
              })}
            </p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Package className="h-4 w-4 text-amber-500" />
              {t("cashFlow.outflowBreakdown.materials")}
            </div>
            <div className="mt-1 text-xl font-semibold">
              {formatCurrency(data?.materialsTotal ?? 0, currency)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("cashFlow.outflowBreakdown.materialsHint")}
            </p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Receipt className="h-4 w-4 text-rose-500" />
              {t("cashFlow.outflowBreakdown.expensesPaid")}
            </div>
            <div className="mt-1 text-xl font-semibold">
              {formatCurrency(data?.expensesTotal ?? 0, currency)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("cashFlow.outflowBreakdown.expensesPending", {
                value: formatCurrency(data?.pendingExpenses ?? 0, currency),
              })}
            </p>
          </Card>
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold">{t("cashFlow.chart.title")}</h2>
              <p className="text-xs text-muted-foreground">{t("cashFlow.chart.subtitle")}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-emerald-500" /> {t("cashFlow.chart.inflows")}
              </span>
              <span className="flex items-center gap-1">
                <TrendingDown className="h-3 w-3 text-rose-500" /> {t("cashFlow.chart.outflows")}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="text-xs font-medium text-muted-foreground">
              {t("cashFlow.chart.models")}
            </div>
            <CashFlowChartTypePicker
              value={chartVariant}
              onChange={setChartVariant}
              accentColor={chartTheme.palette[0]}
            />
          </div>
        </div>
        <div className="h-72 w-full">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t("cashFlow.chart.loading")}
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t("cashFlow.chart.empty")}
            </div>
          ) : (
            <CashFlowChart
              variant={chartVariant}
              data={chartData}
              currency={currency}
              theme={chartTheme}
              isDark={isDark}
              formatCurrency={formatCurrency}
              onPointClick={
                period === "1m" && selectedWeek == null
                  ? (index: number) => {
                      const week = monthWeeks[index]?.week;
                      if (week != null) setSelectedWeek(week);
                    }
                  : undefined
              }
            />
          )}
        </div>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="mb-3 text-sm font-semibold">{t("cashFlow.recent.inflowsTitle")}</h2>
          {(data?.recentInflows ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("cashFlow.recent.noInflows")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {data!.recentInflows.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {p.client_name ?? t("cashFlow.recent.client")}
                      {p.invoice_number ? ` • ${p.invoice_number}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {p.date} • {p.method}
                      {p.reference ? ` • ${p.reference}` : ""}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-emerald-600">
                    +{formatCurrency(p.amount, currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="mb-3 text-sm font-semibold">{t("cashFlow.recent.outflowsTitle")}</h2>
          {(data?.recentOutflows ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("cashFlow.recent.noOutflows")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {data!.recentOutflows.map((o) => (
                <li
                  key={`${o.kind}-${o.id}`}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        {o.kind === "expense"
                          ? t("cashFlow.recent.kindExpense")
                          : o.kind === "payroll"
                            ? t("cashFlow.recent.kindPayroll")
                            : t("cashFlow.recent.kindMaterial")}
                      </Badge>
                      <p className="truncate text-sm font-medium">{o.label}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {o.date}
                      {o.project_name ? ` • ${o.project_name}` : ""}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-rose-600">
                    -{formatCurrency(o.amount, currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
